"use client";
import { formatDisplayDate } from "@/lib/date-format";
import { miraNumber } from "@/lib/mira-number";

import { useState } from "react";
import { DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";

function loadImage(source: string | undefined) {
  return new Promise<HTMLImageElement | undefined>((resolve) => {
    if (!source) return resolve(undefined);
    const image = new Image();
    const timeout = window.setTimeout(() => resolve(undefined), 10_000);
    image.crossOrigin = "anonymous";
    image.onload = () => { window.clearTimeout(timeout); resolve(image); };
    image.onerror = () => { window.clearTimeout(timeout); resolve(undefined); };
    image.src = new URL(source, window.location.origin).href;
  });
}

function safeFileName(value: string) {
  return value.replace(/[\\/:*?"<>|]/gu, "-").slice(0, 80) || "形象";
}

export function PlazaCertificateDownload({
  id,
  title,
  imageUrl,
  publishedAt,
  buttonLabel = "形象登记证书",
}: {
  id: string;
  title: string;
  imageUrl?: string;
  publishedAt: number;
  buttonLabel?: string;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  async function download() {
    setPending(true);
    setError("");
    try {
      const width = 660;
      const height = 960;
      const canvas = document.createElement("canvas");
      canvas.width = width * 2;
      canvas.height = height * 2;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("CANVAS_UNAVAILABLE");
      context.scale(2, 2);

      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#111A48");
      gradient.addColorStop(1, "#24124F");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
      context.strokeStyle = "#8FA6FF";
      context.lineWidth = 3;
      context.strokeRect(18, 18, width - 36, height - 36);
      context.strokeStyle = "rgba(255,255,255,.18)";
      context.lineWidth = 1;
      context.strokeRect(30, 30, width - 60, height - 60);

      context.textAlign = "center";
      context.fillStyle = "#9DB4FF";
      context.font = "600 24px sans-serif";
      context.fillText("MIRAFANS · 镜界", width / 2, 92);
      context.fillStyle = "#FFFFFF";
      context.font = "700 44px sans-serif";
      context.fillText("形象登记凭证", width / 2, 152);
      context.fillStyle = "#AEB4D6";
      context.font = "20px sans-serif";
      context.fillText("AVATAR REGISTRATION CERTIFICATE", width / 2, 188);

      const image = await loadImage(imageUrl);
      context.save();
      context.beginPath();
      context.arc(width / 2, 330, 104, 0, Math.PI * 2);
      context.clip();
      context.fillStyle = "#5564B5";
      context.fillRect(width / 2 - 104, 226, 208, 208);
      if (image) {
        const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
        context.drawImage(
          image,
          (image.naturalWidth - sourceSize) / 2,
          (image.naturalHeight - sourceSize) / 2,
          sourceSize,
          sourceSize,
          width / 2 - 104,
          226,
          208,
          208,
        );
      } else {
        context.fillStyle = "#FFFFFF";
        context.font = "700 72px sans-serif";
        context.fillText("M", width / 2, 354);
      }
      context.restore();
      context.strokeStyle = "#C2CCFF";
      context.lineWidth = 5;
      context.beginPath();
      context.arc(width / 2, 330, 108, 0, Math.PI * 2);
      context.stroke();

      context.fillStyle = "#FFFFFF";
      context.font = "700 34px sans-serif";
      context.fillText(title || "未命名形象", width / 2, 500, 480);
      context.fillStyle = "#38E0A6";
      context.font = "600 24px sans-serif";
      context.fillText("平台已审核 · 公开展示", width / 2, 548);

      context.textAlign = "left";
      context.fillStyle = "#8992BC";
      context.font = "20px sans-serif";
      context.fillText("凭证编号", 90, 638);
      context.fillText("公开日期", 90, 710);
      context.fillStyle = "#F4F6FF";
      context.font = "600 18px monospace";
      context.fillText(miraNumber(id), 230, 638, 340);
      context.font = "600 24px sans-serif";
      context.fillText(publishedAt > 0 ? formatDisplayDate(new Date(publishedAt * 1_000), { year: "numeric", month: "2-digit", day: "2-digit" }) : "日期未记录", 230, 710);

      context.strokeStyle = "rgba(255,255,255,.15)";
      context.beginPath();
      context.moveTo(90, 770);
      context.lineTo(width - 90, 770);
      context.stroke();
      context.textAlign = "center";
      context.fillStyle = "#AEB4D6";
      context.font = "18px sans-serif";
      context.fillText("本凭证展示网站当前公开登记状态", width / 2, 824);
      context.fillStyle = "#737CA8";
      context.font = "16px sans-serif";
      context.fillText("仅证明该形象已在平台登记，不代表真人活体、", width / 2, 872);
      context.fillText("法律权属或政府认证", width / 2, 900);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("PNG_UNAVAILABLE");
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `${safeFileName(title)}-形象登记凭证.png`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
    } catch {
      setError("证书生成失败，请刷新页面后重试");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mt-5">
      <Button type="button" variant="secondary" size="lg" className="w-full" disabled={pending} onClick={() => void download()}>
        <DownloadSimple size={18} weight="bold" aria-hidden="true" />
        {pending ? "正在生成证书…" : buttonLabel}
      </Button>
      {error && <p role="alert" className="mt-2 text-xs text-[var(--color-pending)]">{error}</p>}
    </div>
  );
}
